import { useMemo } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import styled from '@emotion/styled';
import useInputDebounce from '../../hooks/useInputDebounce';

const CustomAutocomplete = styled(Autocomplete)``;

const AutoCompleteMulti = ({
  // Controlled Props
  value,
  onChange,
  error,
  helperText,
  name,
  
  // Configuration Props
  fieldName: optionField, 
  displayField,
  displayLabel, 
  placeholder = "Search", 
  size = "small", 
  url, 
  serverOptions, 
  forceServer = false, 
  conditionStr, 
  multiple = true,
  style,
  additionalCollections = [],
  ...props
}) => {
  const renderLabel = displayField || optionField;

  // Handles API fetching and debouncing based on input
  const { handleInputChange, loading, options, value: inputValue, setValue } = useInputDebounce({ 
    url, 
    state: true, 
    serverOptions, 
    conditionStr, 
    forceServer,
    additionalCollections 
  });

  // Maps the IDs passed in 'value' to the full objects needed by MUI
  const muiValue = useMemo(() => {
    if (multiple) {
      const arrayValue = Array.isArray(value) ? value : [];
      return options.filter(opt => 
        arrayValue.map(v => v?.toString()).includes(opt[optionField]?.toString())
      );
    }
    return options.find(el => el[optionField]?.toString() === value?.toString()) || null;
  }, [value, options, multiple, optionField]);

  const handleChangeInput = (e, val, reason) => {
    if (["input", "clear", "selectOption"].includes(reason)) {
      setValue(val);
      if (handleInputChange) {
        handleInputChange(val, e);
      }
    }
  };

  const handleSelectionChange = (e, newValue) => {
    if (multiple) {
      // Returns an array of IDs/Keys to the parent
      const selectedIds = (newValue || []).map(val => 
        typeof val === 'string' ? val : val[optionField]?.toString()
      );
      if (onChange) onChange(selectedIds);
    } else {
      // Returns a single ID/Key to the parent
      const finalVal = newValue ? (typeof newValue === 'string' ? newValue : newValue[optionField]?.toString()) : null;
      if (onChange) onChange(finalVal, newValue);
    }
  };

  return (
    <CustomAutocomplete
      {...props}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      loading={loading}
      options={options}
      size={size}
      value={muiValue} 
      inputValue={inputValue}
      onInputChange={handleChangeInput}
      onChange={handleSelectionChange}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option[renderLabel]?.toString() || "";
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const label = displayLabel ? displayLabel(option) : option[renderLabel];
        return (
          <li key={key} {...optionProps}>
            {label}
          </li>
        );
      }}
      fullWidth
      freeSolo={false}
      clearOnBlur
      selectOnFocus
      // sx={{bgcolor: "green"}}
      renderInput={(params) => (
        <TextField 
          {...params} 
          name={name}
          error={!!error}
          helperText={helperText}
          placeholder={placeholder} 
          variant="outlined" 
          style={style} 
        />
      )}
    />
  );
};

export default AutoCompleteMulti;